
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminSettings } from "@/types/adminSettings";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw } from "lucide-react";

interface RawSettingsTabProps {
  settings: AdminSettings;
}

export const RawSettingsTab: React.FC<RawSettingsTabProps> = ({ settings }) => {
  const { toast } = useToast();
  const [rawSettings, setRawSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRawSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .eq('id', 1)
        .single();
      
      if (error) throw error;
      
      setRawSettings(data);
      toast({
        title: "Settings loaded",
        description: "Raw settings data fetched from database"
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading settings",
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Raw Settings Data</CardTitle>
        <CardDescription>
          View the raw settings data from the application state and database
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <Button 
            onClick={fetchRawSettings} 
            variant="outline" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Fetch Raw Database Settings
              </>
            )}
          </Button>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-medium">Current Settings Object (from React State)</h3>
            <div className="bg-muted p-4 rounded-md">
              <ScrollArea className="h-[200px]">
                <pre className="text-xs">{JSON.stringify(settings, null, 2)}</pre>
              </ScrollArea>
            </div>
          </div>

          {rawSettings && (
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Raw Database Settings</h3>
              <div className="bg-muted p-4 rounded-md">
                <ScrollArea className="h-[200px]">
                  <pre className="text-xs">{JSON.stringify(rawSettings, null, 2)}</pre>
                </ScrollArea>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
